import type { Children } from '../../types';
import type { CursorProps } from './Cursor';

import * as React from 'react';
import { Cursor } from './Cursor';

interface CursorContextInterface {
  enabled: boolean;
  cursor: CursorProps;
  setCursor: React.Dispatch<React.SetStateAction<{ active: number }>>;
  panel: boolean;
}

interface CursorContextProviderProps extends Children {
  enable: boolean;
}

const CursorContext = React.createContext(null as any);
export const useCursorContext = (): CursorContextInterface => {
  const context = React.useContext(CursorContext);
  if (context === undefined) {
    throw new Error('useCursorContext must be used within CursorContextProvider.');
  }

  return context as CursorContextInterface;
};

export const CursorContextProvider = (props: CursorContextProviderProps) => {
  const { children, enable } = props;
  const [cursor, setCursor] = React.useState<CursorProps>({ active: 0 });
  const [enabled, setEnabled] = React.useState<boolean>(false);
  const [panel, setPanel] = React.useState<boolean>(false);

  const messageHandler = (event: MessageEvent) => {
    if (event.data?.name === 'orbit:change:editing' && event.data?.data) {
      setEnabled(JSON.parse(event.data.data));
    }
    if (event.data?.name === 'orbit:change:panel' && event.data?.data) {
      setPanel(JSON.parse(event.data.data).panel);
    }
    if (event.data?.name === 'orbit:prevent:card-scroll' && event.data?.data) {
      const { preventCardScroll } = JSON.parse(event.data.data);

      if (preventCardScroll) {
        // Add global click handler to prevent scroll-to-top on card clicks
        const handleCardClick = (e: Event) => {
          const target = e.target as HTMLElement;

          // Check if click is on a Card component (MUI Card with CardActionArea)
          const cardActionArea = target.closest('.MuiCardActionArea-root');
          const cardElement = target.closest('.MuiCard-root');

          if (cardActionArea && cardElement) {
            // Find any Link components within the card that might cause scrolling
            const linkElement = cardActionArea.querySelector(
              'a[href^="/game/"], a[href^="#"], a[href$="#"], a[href=""]'
            );

            if (linkElement) {
              // Prevent the default link behavior that causes scroll-to-top
              e.preventDefault();
              e.stopPropagation();
            }
          }
        };

        // Use capture phase to catch events before they bubble
        document.addEventListener('click', handleCardClick, true);

        // Store the handler for cleanup
        (window as any).__cardScrollHandler = handleCardClick;
      } else {
        // Remove the handler when not in editing mode
        if ((window as any).__cardScrollHandler) {
          document.removeEventListener('click', (window as any).__cardScrollHandler, true);
          delete (window as any).__cardScrollHandler;
        }
      }
    }
  };

  React.useEffect(() => {
    setEnabled(enable);
  }, [enable]);

  React.useEffect(() => {
    window.addEventListener('message', messageHandler);
    return () => {
      window.removeEventListener('message', messageHandler);
    };
  }, []);

  const cursorContextValues: CursorContextInterface = {
    enabled,
    cursor,
    setCursor,
    panel,
  };

  return (
    <CursorContext.Provider value={cursorContextValues}>
      <Cursor />
      {children}
    </CursorContext.Provider>
  );
};
